import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ShoppingBag, FileText, Clock, Download, ChevronRight, Package, ShoppingCart } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { ordersApi, Order as ApiOrder } from '../api/orders';
import { CartItem } from '../context/AppContext';

function transformApiOrder(apiOrder: ApiOrder) {
  return {
    id: apiOrder.order_number,
    type: apiOrder.type,
    items: apiOrder.items.map(item => ({
      product: {
        id: String(item.product_id),
        category: 'vetements' as const,
        subcategory: 'vestes' as const,
        sector: [],
        name: `Product ${item.product_id}`,
        nameEn: `Product ${item.product_id}`,
        brand: '',
        ref: '',
        price: item.unit_price,
        currency: 'EUR',
        stock: 0,
        moq: 1,
        quoteThreshold: 0,
        norm: [],
        description: '',
        descriptionEn: '',
        images: [],
        variants: {},
        features: [],
        featuresEn: [],
      },
      qty: item.quantity,
    })) as CartItem[],
    date: apiOrder.created_at,
    status: apiOrder.status,
    form: apiOrder.form_data || {},
    total: apiOrder.total_amount,
    itemCount: apiOrder.items.reduce((sum, item) => sum + item.quantity, 0),
  };
}

export default function ResellerOrders() {
  usePageTitle("Mes commandes revendeur — Lodiss");

  const { lang, setPage, addToCart } = useApp();
  const T = t[lang];

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);

  useEffect(() => {
    ordersApi.getOrders()
      .then(data => {
        setOrders(data.map(transformApiOrder));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleReorder = (order: any) => {
    order.items.forEach((item: any) => {
      addToCart({ product: item.product, qty: item.qty });
    });
    setPage('cart');
  };

  const handleDownloadInvoice = async (orderId: string) => {
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${API_BASE}/orders/${orderId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('lodiss_auth_token')?.replace(/"/g, '')}`,
        },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (err) {
      console.error('Failed to download invoice:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'confirmed': return 'bg-emerald-50 text-emerald-700';
      case 'processing': return 'bg-blue-50 text-blue-700';
      case 'shipped': return 'bg-indigo-50 text-indigo-700';
      case 'delivered': return 'bg-gray-50 text-gray-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, Record<string, string>> = {
      pending: { fr: 'En attente', en: 'Pending' },
      confirmed: { fr: 'Confirmée', en: 'Confirmed' },
      processing: { fr: 'En cours', en: 'Processing' },
      shipped: { fr: 'Expédiée', en: 'Shipped' },
      delivered: { fr: 'Livrée', en: 'Delivered' },
      cancelled: { fr: 'Annulée', en: 'Cancelled' },
    };
    return labels[status]?.[lang] || status;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-950 mb-6">
          {lang === 'fr' ? 'Mes commandes' : 'My orders'}
        </h1>

        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <ShoppingBag size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm mb-5">
              {lang === 'fr' ? 'Aucune commande' : 'No orders yet'}
            </p>
            <button
              onClick={() => setPage('reseller-catalogue')}
              className="px-5 py-2.5 bg-lodiss-red hover:bg-lodiss-dark text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {lang === 'fr' ? 'Passer une commande' : 'Place an order'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    {order.type === 'direct' ? (
                      <ShoppingBag size={18} className="text-lodiss-red" />
                    ) : (
                      <FileText size={18} className="text-amber-500" />
                    )}
                    <div>
                      <span className="text-sm font-bold text-gray-900 font-mono">{order.id}</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {order.type === 'direct' ? (lang === 'fr' ? 'Commande' : 'Order') : (lang === 'fr' ? 'Devis' : 'Quote')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownloadInvoice(order.id)}
                      className="p-2 text-gray-400 hover:text-lodiss-red transition-colors"
                      title={lang === 'fr' ? 'Télécharger la facture' : 'Download invoice'}
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <ChevronRight size={18} className={`transition-transform ${selectedOrder?.id === order.id ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock size={14} />
                    {formatDate(order.date)}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500">
                      {order.itemCount} {lang === 'fr' ? 'articles' : 'items'}
                    </span>
                    <span className="font-bold text-gray-900">
                      {formatPrice(order.total || 0)}
                    </span>
                  </div>
                </div>

                {selectedOrder?.id === order.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="space-y-3 mb-4">
                      {order.items.map((item: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package size={20} className="text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.product.name}</p>
                            <p className="text-xs text-gray-500">Réf: {item.product.ref}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">× {item.qty}</p>
                            <p className="text-xs text-gray-500">{formatPrice(item.product.price * item.qty)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => handleReorder(order)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-lodiss-red text-lodiss-red font-semibold rounded-lg hover:bg-lodiss-red hover:text-white transition-colors"
                    >
                      <ShoppingCart size={16} />
                      {lang === 'fr' ? 'Commander à nouveau' : 'Reorder'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
